import { useAuth } from '@/contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Trophy, HelpCircle, BarChart3, User, Shield, LogOut, Menu, X, Users, Globe, Layers, Building2, MapPin, Swords, Target, BookOpen, Gift, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_c66cef96-8aba-458e-b39b-120deb34b863/artifacts/in70evo4_QUINIELA%20LOGO.png';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(true);
  const isAdmin = user?.role === 'admin';

  const userNavItems = [
    { path: '/', icon: Home, label: 'Inicio' },
    { path: '/partidos', icon: Trophy, label: 'Partidos' },
    { path: '/predicciones', icon: HelpCircle, label: 'Predicciones' },
    { path: '/ranking', icon: BarChart3, label: 'Ranking' },
    { path: '/reglas', icon: BookOpen, label: 'Reglas' },
    { path: '/premios', icon: Gift, label: 'Premios' },
    { path: '/perfil', icon: User, label: 'Perfil' },
  ];

  const adminNavItems = [
    { path: '/', icon: Home, label: 'Inicio' },
    { path: '/partidos', icon: Trophy, label: 'Partidos' },
    { path: '/ranking', icon: BarChart3, label: 'Ranking' },
    { path: '/perfil', icon: User, label: 'Perfil' },
    { path: '/admin', icon: Shield, label: 'Admin' },
  ];

  const adminSubItems = [
    { path: '/admin', icon: Shield, label: 'Panel Admin' },
    { path: '/admin/usuarios', icon: Users, label: 'Usuarios' },
    { path: '/admin/paises', icon: Globe, label: 'Paises' },
    { path: '/admin/etapas', icon: Layers, label: 'Etapas' },
    { path: '/admin/estadios', icon: Building2, label: 'Estadios' },
    { path: '/admin/sedes', icon: MapPin, label: 'Sedes' },
    { path: '/admin/partidos', icon: Swords, label: 'Partidos' },
    { path: '/admin/resultados', icon: Target, label: 'Resultados' },
    { path: '/admin/predicciones', icon: HelpCircle, label: 'Predicciones' },
    { path: '/admin/reglas', icon: BookOpen, label: 'Reglas' },
    { path: '/admin/premios', icon: Gift, label: 'Premios' },
  ];

  const mobileNavItems = isAdmin ? adminNavItems : userNavItems;
  const sidebarMainItems = isAdmin
    ? [
        { path: '/', icon: Home, label: 'Inicio' },
        { path: '/partidos', icon: Trophy, label: 'Partidos' },
        { path: '/ranking', icon: BarChart3, label: 'Ranking' },
        { path: '/perfil', icon: User, label: 'Perfil' },
      ]
    : userNavItems;

  const handleNav = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex" data-testid="app-layout">

      {/* ═══ DESKTOP SIDEBAR (lg+) ═══ */}
      <aside className="hidden lg:flex flex-col w-64 fixed top-0 left-0 h-screen bg-[#0D1425] border-r border-[rgb(244, 227, 158)]/30 z-50">
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-[rgb(244, 227, 158)]/20">
          <img src={LOGO_URL} alt="Logo" className="h-11 w-11 object-contain cursor-pointer" onClick={() => navigate('/')} />
          <div>
            <h1 className="text-white font-bold text-sm leading-tight" style={{ fontFamily: 'Montserrat' }}>Quiniela</h1>
            <h1 className="gold-text font-extrabold text-sm leading-tight" style={{ fontFamily: 'Montserrat' }}>Mundial 2026</h1>
          </div>
        </div>

        {/* Main Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {sidebarMainItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              data-testid={`sidebar-${item.label.toLowerCase()}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                isActive(item.path) && !location.pathname.startsWith('/admin')
                  ? 'bg-[rgb(244, 227, 158)]/15 text-[rgb(244, 227, 158)] border border-[rgb(244, 227, 158)]/30'
                  : 'text-white/60 hover:bg-white/5 hover:text-white border border-transparent'
              }`}
            >
              <item.icon size={18} />
              {item.label}
            </button>
          ))}

          {/* Admin Section */}
          {isAdmin && (
            <div className="pt-3 mt-3 border-t border-[rgb(244, 227, 158)]/15">
              <button
                onClick={() => setAdminExpanded(!adminExpanded)}
                className="w-full flex items-center justify-between px-4 py-2 gold-text text-xs font-bold uppercase tracking-wider"
              >
                <span>Administracion</span>
                {adminExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {adminExpanded && (
                <div className="space-y-0.5 mt-1">
                  {adminSubItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      data-testid={`sidebar-admin-${item.path.split('/').pop()}`}
                      className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                        location.pathname === item.path
                          ? 'bg-[rgb(244, 227, 158)]/15 text-[rgb(244, 227, 158)]'
                          : 'text-white/50 hover:bg-white/5 hover:text-white/80'
                      }`}
                    >
                      <item.icon size={15} />
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* User Info + Logout */}
        <div className="px-4 py-4 border-t border-[rgb(244, 227, 158)]/20">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full gold-gradient flex items-center justify-center flex-shrink-0">
              <User size={16} className="text-[#0A0F1C]" />
            </div>
            <div className="min-w-0">
              <p className="text-white font-semibold text-sm truncate">{user?.user_id}</p>
              <p className="text-[rgb(244, 227, 158)] text-[10px] font-medium">{isAdmin ? 'Admin' : 'VIP'}</p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="sidebar-logout"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-[rgb(244, 227, 158)]/30 text-white/50 hover:bg-red-500/15 hover:text-red-400 hover:border-red-500/30 transition-all text-xs font-medium"
          >
            <LogOut size={14} /> Cerrar Sesion
          </button>
        </div>
      </aside>

      {/* ═══ MOBILE HEADER (< lg) ═══ */}
      <div className="flex flex-col flex-1 lg:ml-64">
        <header className="sticky top-0 z-50 bg-[#111A30]/90 backdrop-blur-xl border-b border-[rgb(244, 227, 158)]/40 px-4 py-3 flex items-center justify-between lg:hidden">
          <img src={LOGO_URL} alt="Logo" className="h-10 w-10 object-contain cursor-pointer" onClick={() => navigate('/')} />
          <h1 className="font-bold text-white text-lg tracking-wide" style={{ fontFamily: 'Montserrat' }}>
            {isAdmin ? 'Panel Admin' : 'Panel'}
          </h1>
          <div className="flex items-center gap-3">
            <button onClick={logout} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[rgb(244, 227, 158)]/40 bg-white/5 hover:bg-red-500/20 text-white/60 hover:text-red-400 transition-all text-xs font-medium" data-testid="logout-button">
              <LogOut size={16} /> Salir
            </button>
            {isAdmin && (
              <button onClick={() => setMenuOpen(!menuOpen)} className="text-white/50 hover:text-white transition-colors" data-testid="menu-button">
                {menuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            )}
          </div>
        </header>

        {/* Mobile Admin Side Menu overlay */}
        {menuOpen && isAdmin && (
          <div className="fixed inset-0 z-40 flex lg:hidden" data-testid="admin-menu">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMenuOpen(false)} />
            <div className="relative ml-auto w-72 h-full bg-[#111A30] border-l border-[rgb(244, 227, 158)]/30 p-6 flex flex-col gap-2 overflow-y-auto">
              <h2 className="text-[rgb(244, 227, 158)] font-bold text-lg mb-4" style={{ fontFamily: 'Montserrat' }}>Menu de Administracion</h2>
              {adminSubItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNav(item.path)}
                  className={`text-left px-4 py-3 rounded-xl transition-all flex items-center gap-3 ${
                    location.pathname === item.path
                      ? 'bg-[rgb(244, 227, 158)]/20 text-[rgb(244, 227, 158)] font-semibold'
                      : 'text-white/70 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <item.icon size={16} />
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ═══ CONTENT ═══ */}
        <main className="flex-1 overflow-y-auto pb-24 lg:pb-8 px-4 py-4 lg:px-8 lg:py-6">
          <div className="max-w-6xl mx-auto">
            {children}
          </div>
        </main>

        {/* ═══ MOBILE BOTTOM NAV (< lg) ═══ */}
        <nav className="fixed bottom-0 left-0 right-0 bg-[#111A30]/95 backdrop-blur-xl border-t border-[rgb(244, 227, 158)]/40 px-2 py-2 flex justify-around items-center z-50 lg:hidden" data-testid="bottom-nav">
          {mobileNavItems.map((item) => {
            const active = item.path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex flex-col items-center gap-1 px-3 py-1 rounded-lg transition-all ${
                  active ? 'text-[rgb(244, 227, 158)]' : 'text-white/40 hover:text-white/70'
                }`}
              >
                <item.icon size={20} />
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
